import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import '../styles/coastal-dusk.css';

const PaymentPage = () => {
  const navigate = useNavigate();
  const { token, user, API } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMusicPrompt, setShowMusicPrompt] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const musicRef = useRef(null);
  const emailInputRef = useRef(null);
  const [guestEmail, setGuestEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState('access_24h');

  const pricingTiers = [
    { id: 'access_24h', amount: 18, label: '$18' },
    { id: 'access_24h_22', amount: 22, label: '$22' },
    { id: 'access_24h_35', amount: 35, label: '$35' },
  ];

  const contactEmail = 'info@clarityjournalbyenquips.com';

  const toggleMusic = () => {
    if (musicRef.current) {
      if (musicRef.current.paused) {
        musicRef.current.play().then(() => {
          setIsMusicPlaying(true);
          setShowMusicPrompt(false);
        }).catch(e => console.log('Could not play music:', e));
      } else {
        musicRef.current.pause();
        setIsMusicPlaying(false);
      }
    }
  };

  useEffect(() => {
    const playMusic = async () => {
      if (musicRef.current) {
        try {
          musicRef.current.volume = 0.7;
          await musicRef.current.play();
          setIsMusicPlaying(true);
        } catch (err) {
          setShowMusicPrompt(true);
        }
      }
    };
    playMusic();
  }, []);

  const handleCheckout = async () => {
    let emailValue = guestEmail;
    if (emailInputRef.current) {
      emailValue = emailInputRef.current.value.toLowerCase();
      setGuestEmail(emailValue);
    }

    if (!token && !showEmailInput) {
      setShowEmailInput(true);
      return;
    }

    if (!token && !emailValue) {
      setError('Please enter your email address');
      return;
    }

    if (!token && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      const payload = { 
        package_id: selectedPrice,
        origin_url: originUrl 
      };

      if (!token) {
        payload.guest_email = emailValue;
      }

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/payment/create-checkout`,
        payload,
        { headers }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate checkout');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--grey-deep)' }}>
      {/* Blurred Background Image */}
      <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
      
      {/* Gradient Orbs */}
      <div className="landing-orb cool focus-shift" style={{ '--orb-opacity': '0.5', top: '-5%', right: '-10%', width: '50vw', height: '50vw', maxWidth: '550px', maxHeight: '550px' }} />
      <div className="landing-orb neutral" style={{ '--orb-opacity': '0.35', top: '40%', left: '-8%', width: '45vw', height: '45vw', maxWidth: '500px', maxHeight: '500px', animationDelay: '-8s' }} />
      <div className="landing-orb cool" style={{ '--orb-opacity': '0.25', top: '75%', right: '-5%', width: '40vw', height: '40vw', maxWidth: '450px', maxHeight: '450px', animationDelay: '-14s' }} />

      {/* Background Music */}
      <audio ref={musicRef} src="/assets/landing-music.mp3" loop autoPlay />

      {/* Music Toggle */}
      <button 
        className="music-toggle-coastal"
        onClick={toggleMusic}
        data-testid="music-toggle"
        style={{ background: 'rgba(26, 26, 26, 0.45)', border: '1px solid rgba(163, 163, 163, 0.08)' }}
      >
        <span style={{ fontWeight: 300 }}>{isMusicPlaying ? 'Playing' : 'Music'}</span>
      </button>

      {/* Music Prompt */}
      {showMusicPrompt && (
        <div 
          className="music-prompt"
          onClick={async () => {
            if (musicRef.current && musicRef.current.paused) {
              try {
                musicRef.current.volume = 0.7;
                await musicRef.current.play();
                setIsMusicPlaying(true);
                setShowMusicPrompt(false);
              } catch (e) {
                console.error('Failed to start music:', e);
              }
            }
          }}
          style={{ 
            background: 'rgba(26, 26, 26, 0.6)', border: '1px solid rgba(163, 163, 163, 0.08)',
            whiteSpace: 'nowrap', padding: '12px 24px', fontSize: '0.82rem'
          }}
        >
          <span style={{ fontWeight: 300 }}>Tap to start background music</span>
        </div>
      )}

      {/* ========== HERO ========== */}
      <section className="pt-28 pb-16 px-6 sm:px-10">
        <div className="max-w-xl mx-auto relative z-10">
          <div className="editorial-label headline-focus-in mb-6" style={{ visibility: 'hidden', height: 0, margin: 0, padding: 0, overflow: 'hidden' }}>THE SESSION</div>
          <h1 className="editorial-title headline-focus-in-d1 mb-5" style={{ fontSize: 'clamp(2.2rem, 7vw, 3.4rem)' }}>
            Thank you
          </h1>
          <p className="editorial-subtitle headline-focus-in-d2" style={{ maxWidth: '400px' }}>
            for letting us walk beside you during this mini clarity journey.
          </p>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== PRICING ========== */}
      <section style={{ padding: '80px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 text-center relative z-10">
          <div className="landing-step-label" style={{ textAlign: 'center' }}>SESSION FEE</div>
          <h2 className="landing-step-heading text-center" style={{ marginBottom: '32px' }}>The Session Fee</h2>
          
          {/* Contribution Context */}
          <div className="text-left mb-10">
            <p className="landing-step-body" style={{ marginBottom: '16px' }}>
              If contribution is one of the self-care rituals you find helps prepare your 
              mind well for any occasion, simply select one of the pricings below. The 
              portion above the $18 session fee will be periodically sent to the Red Cross 
              under "Clients of Clarity Journal."
            </p>
            <p className="landing-step-body" style={{ color: 'var(--text-primary)' }}>
              All pricing selections provide the same full access to everything the 
              Clarity Journal offers.
            </p>
          </div>

          {/* Pricing Tier Selection */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            {pricingTiers.map((tier) => (
              <button
                key={tier.id}
                onClick={() => setSelectedPrice(tier.id)}
                className="cta-pill-landing"
                style={{
                  padding: '16px 40px',
                  fontSize: '1.2rem',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 400,
                  letterSpacing: '0.04em',
                  width: '100%',
                  maxWidth: '220px',
                  ...(selectedPrice === tier.id ? {
                    borderColor: 'var(--accent-teal)',
                    boxShadow: '0 0 24px 6px rgba(184, 212, 208, 0.12)',
                    color: 'var(--text-primary)',
                  } : {
                    borderColor: 'rgba(163, 163, 163, 0.15)',
                    color: 'rgba(163, 163, 163, 0.5)',
                  })
                }}
                data-testid={`price-option-${tier.amount}`}
              >
                {tier.label} <span style={{ fontSize: '0.65rem', opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase', marginLeft: '4px', fontWeight: 300 }}>USD</span>
              </button>
            ))}
          </div>
          
          {error && (
            <div className="message-error mb-6" data-testid="payment-error">
              {error}
            </div>
          )}

          {/* Guest Email Input */}
          {!token && showEmailInput && (
            <div className="landing-form-card max-w-sm mx-auto mb-8">
              <label className="landing-form-label" style={{ color: '#ffffff', fontSize: '1rem' }}>Your Email</label>
              <input
                ref={emailInputRef}
                type="email"
                defaultValue={guestEmail}
                onBlur={(e) => setGuestEmail(e.target.value.toLowerCase())}
                onInput={(e) => {
                  e.target.value = e.target.value.toLowerCase();
                }}
                placeholder="your@email.com"
                className="landing-form-input text-center"
                disabled={loading}
                data-testid="guest-email-input"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="email"
                spellCheck="false"
                inputMode="email"
              />
            </div>
          )}

          <button
            onClick={handleCheckout}
            disabled={loading}
            className="cta-pill-landing"
            style={{ padding: '16px 48px', fontSize: '0.88rem' }}
            data-testid="checkout-btn"
          >
            {loading ? 'Processing...' : 'Proceed to Checkout'}
          </button>

          <div className="mt-10">
            <button
              onClick={() => navigate('/')}
              className="landing-ghost-link"
              data-testid="back-to-main"
              style={{
                fontSize: '0.88rem',
                color: 'var(--accent-teal)',
                opacity: 0.8,
                borderBottom: '1px solid rgba(184, 212, 208, 0.3)',
                paddingBottom: '2px',
              }}
            >
              Back to the main page
            </button>
          </div>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== REFUND ========== */}
      <section style={{ padding: '60px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <div className="landing-step-label">REFUND</div>
          <p className="landing-step-body">
            This journaling experience is delivered instantly, the content becomes 
            immediately accessible. Its value cannot be returned or revoked, and a refund 
            is therefore not applicable.
          </p>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== GETTING STARTED ========== */}
      <section style={{ padding: '60px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <div className="landing-step-label">GETTING STARTED</div>
          <h2 className="landing-step-heading">Before you begin</h2>
          <div className="space-y-5">
            <p className="landing-step-body">
              If you have already created a login account with us, your 24-hour session 
              will begin once you log in after the session fee is processed.
            </p>
            <p className="landing-step-body">
              If you have not yet created a login account, you will be prompted to set 
              a password after completing your purchase. Your 24-hour journaling session 
              will start immediately afterward.
            </p>
            <p className="landing-step-body">
              If you prefer to start your 24-hour session at a later time, simply create 
              your password whenever you are ready.
            </p>
            <p className="landing-step-body" style={{ color: 'var(--text-primary)', fontSize: '0.98rem' }}>
              In your first log-in, you might be redirected to the pricing page again. 
              Simply tap "Back to the Main Page" and log in once more to continue.
            </p>
          </div>
        </div>
      </section>

      <div className="landing-tide-line" />

      {/* ========== CONTACT ========== */}
      <section style={{ padding: '60px 0' }}>
        <div className="max-w-xl mx-auto px-6 sm:px-10 relative z-10">
          <p className="landing-step-body" style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>For:</p>
          <div className="landing-step-body" style={{ paddingLeft: '1rem', marginBottom: '20px' }}>
            <p style={{ marginBottom: '8px' }}>a. Gifting Multiple-Session Pass</p>
            <p>b. Returning Members</p>
          </div>
          <p className="landing-step-body">
            Please contact:{' '}
            <a href={`mailto:${contactEmail}`} className="landing-ghost-link" style={{ textDecoration: 'underline' }}>
              {contactEmail}
            </a>
          </p>
        </div>
      </section>

      {/* Spacer */}
      <div className="py-16" />
    </div>
  );
};

export default PaymentPage;
